import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import {
  applyThemeButtonGradientStyles,
  darkenThemeHexColor,
  getThemeButtonGradient,
} from "../../functions/themeAssets";
import "../fonts/breuer-headline.css";
import "./leaderboard.css";

function readSessionUserId() {
  try {
    const raw = sessionStorage.getItem("userData");
    if (!raw) return "";
    const u = JSON.parse(raw);
    const id = u?.userId ?? u?.userid ?? u?.id;
    if (id == null || id === "") return "";
    return String(id).trim();
  } catch {
    return "";
  }
}

function normalizeLeaderboardRow(row, index) {
  if (!row || typeof row !== "object") {
    return {
      key: `row-${index}`,
      rank: index + 1,
      userId: "",
      name: "—",
      points: 0,
      time: "00:00",
    };
  }
  const name =
    row.name ??
    row.player_name ??
    row.user_name ??
    row.display_name ??
    (row.email ? String(row.email).split("@")[0] : null) ??
    "—";
  const rawPoints = row.points ?? row.score ?? row.total_score ?? row.point ?? 0;
  const n = Number(rawPoints);
  const points = Number.isFinite(n) ? n : 0;
  const rawTime = row.time ?? row.display_time ?? "00:00";
  const time = String(rawTime).trim() || "00:00";
  const rankNum = Number(row.rank ?? row.rk);
  const rank = Number.isFinite(rankNum) && rankNum > 0 ? rankNum : index + 1;
  const userId =
    row.userId != null && row.userId !== ""
      ? String(row.userId)
      : row.userid != null && row.userid !== ""
        ? String(row.userid)
        : "";

  return {
    key: String(row.userId ?? row.userid ?? row.rank ?? row.id ?? index),
    rank,
    userId,
    name: String(name),
    points,
    time,
  };
}

const LEADERBOARD_TOP_N = 5;

function buildDisplayRows(rows, currentUserId) {
  if (!rows.length) return [];

  const sorted = [...rows].sort((a, b) => a.rank - b.rank);
  const top = sorted
    .filter((r) => r.rank >= 1 && r.rank <= LEADERBOARD_TOP_N)
    .slice(0, LEADERBOARD_TOP_N);

  if (!currentUserId) return top;

  const userRow = sorted.find(
    (r) => r.userId && String(r.userId) === String(currentUserId)
  );

  if (!userRow || userRow.rank <= LEADERBOARD_TOP_N) return top;

  return [...top, { ...userRow, isPinnedViewer: true }];
}

function UserIcon() {
  return (
    <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
      <circle cx="10" cy="7" r="3.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M3.5 17c1.6-3.2 4-4.8 6.5-4.8S14.9 13.8 16.5 17"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true">
      <path
        d="M10 2.2l2.2 4.5 5 .7-3.6 3.5.9 5-4.5-2.4-4.5 2.4.9-5L2.8 7.4l5-.7L10 2.2z"
        fill="#ffd24a"
        stroke="#ffb020"
        strokeWidth="0.6"
      />
    </svg>
  );
}

function HourglassIcon() {
  return (
    <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true">
      <path
        d="M5 3h10M5 17h10M6 3c0 3.2 2 5 4 7-2 2-4 3.8-4 7h8c0-3.2-2-5-4-7 2-2 4-3.8 4-7"
        fill="none"
        stroke="#ffe27a"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RankMedal({ rank }) {
  return (
    <span
      className={`lb-medal${rank === 1 ? " lb-medal--gold" : ""}`}
      aria-label={`Rank ${rank}`}
    >
      <span className="lb-medal__circle">{rank}</span>
      <span className="lb-medal__ribbon" aria-hidden />
    </span>
  );
}

function hexToRgba(hex, alpha) {
  const h = String(hex || "").replace("#", "");
  if (h.length !== 6) return null;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some((c) => Number.isNaN(c))) return null;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Table chrome (borders, header, panel tint, "you" row glow) recolored from the
 *  theme's button gradient, so the leaderboard matches the active theme instead of
 *  a fixed navy-blue palette. Falls back to the CSS defaults when unconfigured. */
function useLeaderboardThemeVars(themeData) {
  return useMemo(() => {
    const { start, end } = getThemeButtonGradient(themeData);
    if (!start) return null;

    const resolvedEnd = end || darkenThemeHexColor(start, 0.25) || start;
    const headerBase = darkenThemeHexColor(resolvedEnd, 0.35) || resolvedEnd;
    const panelBase = darkenThemeHexColor(resolvedEnd, 0.78) || resolvedEnd;

    return {
      "--lb-accent-start": start,
      "--lb-accent-end": resolvedEnd,
      "--lb-panel": hexToRgba(panelBase, 0.74),
      "--lb-header-bg": hexToRgba(headerBase, 0.95),
      "--lb-border": hexToRgba(start, 0.45),
      "--lb-border-strong": hexToRgba(start, 0.6),
      "--lb-line": hexToRgba(start, 0.28),
      "--lb-you-bg-1": hexToRgba(start, 0.38),
      "--lb-you-bg-2": hexToRgba(resolvedEnd, 0.28),
      "--lb-you-bg-3": hexToRgba(start, 0.22),
      "--lb-you-glow": hexToRgba(start, 0.55),
    };
  }, [themeData]);
}

function Leaderboard() {
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data: themeData, status: themeStatus } = useSelector(
    (s) => s.theme
  );
  const themeVars = useLeaderboardThemeVars(themeData);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(() => readSessionUserId());

  const backendBase = useMemo(
    () => String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, ""),
    []
  );

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme || null }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (themeStatus === "succeeded" && themeData) {
      applyThemeButtonGradientStyles(themeData);
    }
  }, [themeStatus, themeData]);

  useEffect(() => {
    document.body.classList.add("leaderboard-page");
    return () => {
      document.body.classList.remove("leaderboard-page");
    };
  }, []);

  const loadLeaderboard = useCallback(async () => {
    if (!adminToken || !backendBase) {
      setLoading(false);
      setError(!adminToken ? "Not signed in." : "Server URL not configured.");
      return;
    }
    setLoading(true);
    setError(null);
    const uid = readSessionUserId();
    setCurrentUserId(uid);
    try {
      const res = await fetch(`${backendBase}/getLeaderboard`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId: uid || undefined,
          limit: LEADERBOARD_TOP_N,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success && Array.isArray(data.data)) {
        setRows(data.data.map(normalizeLeaderboardRow));
      } else {
        setError(data.error || data.message || "Failed to load leaderboard.");
      }
    } catch (e) {
      setError(e.message || "Failed to load leaderboard.");
    } finally {
      setLoading(false);
    }
  }, [adminToken, backendBase]);

  useEffect(() => {
    loadLeaderboard();
  }, [loadLeaderboard]);

  const displayRows = useMemo(
    () => buildDisplayRows(rows, currentUserId),
    [rows, currentUserId]
  );

  return (
    <div className="lb-page" style={themeVars || undefined}>
      <div className="lb-shell">
        <header className="lb-hero">
          <h1 className="lb-hero__title">Leaderboard</h1>
        </header>

        <section className="lb-board" aria-label="Top scores">
          <div className="lb-board__head" aria-hidden>
            <span className="lb-col lb-col--rank">RANK</span>
            <span className="lb-col lb-col--name">
              <span className="lb-col__icon">
                <UserIcon />
              </span>
              NAME
            </span>
            <span className="lb-col lb-col--points">
              <span className="lb-col__icon">
                <StarIcon />
              </span>
              POINTS
            </span>
            <span className="lb-col lb-col--time">
              <span className="lb-col__icon">
                <HourglassIcon />
              </span>
              TIME
            </span>
          </div>

          {loading ? (
            <p className="lb-status">Loading scores…</p>
          ) : error ? (
            <p className="lb-status lb-status--error">{error}</p>
          ) : rows.length === 0 ? (
            <p className="lb-status">No scores yet.</p>
          ) : (
            <ul className="lb-list">
              {displayRows.map((r) => {
                const isYou =
                  currentUserId &&
                  r.userId &&
                  String(r.userId) === String(currentUserId);
                return (
                  <li
                    key={r.isPinnedViewer ? `pinned-${r.key}` : r.key}
                    className={`lb-row${isYou ? " lb-row--you" : ""}${
                      r.isPinnedViewer ? " lb-row--pinned" : ""
                    }`}
                  >
                    <div className="lb-row__rank">
                      <RankMedal rank={r.rank} />
                    </div>
                    <div className="lb-row__name">
                      <span className="lb-row__name-text">{r.name}</span>
                    </div>
                    <div className="lb-row__points">
                      {r.points.toLocaleString()}
                    </div>
                    <div className="lb-row__time">{r.time}</div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

export default Leaderboard;
